import { api } from '../../../api/client';
import { toWire } from '../../../utils/datetime';

/**
 * Analysis ▸ Daily QC — API contract (DailyQcController, /api/v1/daily-qc).
 * Calculations, pass/fail, validation and scheduling are all server-side
 * (DailyQcEngine / DailyQcSchedule); the screens only display what comes back.
 */

/** Entry control types (the prototype's field types). */
export type DailyQcFieldType = 'Date' | 'Time' | 'DateTime' | 'Numeric' | 'Text' | 'ComboBox' | 'Checkbox' | 'Attachment'
  | 'Employee' | 'Lookup' | 'AutoNumber';

/** Overall entry status as the API returns it. */
export type DailyQcStatus = 'Pass' | 'Fail' | 'Incomplete';

/** Today's schedule state of a logbook. */
export type DailyQcTodayState = 'done' | 'due' | 'overdue' | 'none';

/** One configured logbook field; `name` is the dbo.DailyQC column its value is stored in. */
export interface DailyQcField {
  name: string;
  caption: string;
  type: DailyQcFieldType;
  options: string[];
  default: string | null;
  width: number;
  freeze: boolean;
  symbol: string | null;
  calc: string | null;
  accept: string | null;
  required: boolean;
}

/** A logbook row of the Level 1 list. */
export interface DailyQcLogbook {
  oid: string;
  templateId: number;
  name: string;
  category: string | null;
  instrumentCode: string | null;
  instrumentName: string | null;
  method: string | null;
  schedule: string;
  lastEntry: string | null;   // yyyy-MM-dd
  nextDue: string | null;     // yyyy-MM-dd
  todayState: DailyQcTodayState;
  entryCount: number;
}

/** A numeric limit a field's pass rule sets on its own symbol. */
export interface DailyQcLimit { op: '>=' | '<=' | '>' | '<'; value: number }

/** The template's own captions for the system fields (G5: e.g. Analyst captioned "ArchivedBy"). */
export interface DailyQcLabels { testedBy: string; remark: string; showDailyQcId: boolean; dailyQcId: string }

/** One logbook with its fields, New-entry defaults and chart limits. */
export interface DailyQcLogbookDetail {
  logbook: DailyQcLogbook;
  instructions: string | null;
  notifyOnFailure: boolean;
  fields: DailyQcField[];
  labels: DailyQcLabels | null;
  defaults: Record<string, string | null>;
  limits: Record<string, DailyQcLimit[]>;
  isActive: boolean;
}

/** One entry. Values: Date yyyy-MM-dd · Time HH:mm · DateTime yyyy-MM-ddTHH:mm · Checkbox "1"/"0"/null. */
export interface DailyQcEntry {
  oid: string;
  dailyQcId: string | null;
  values: Record<string, string | null>;
  accept: Record<string, boolean | null>;
  status: DailyQcStatus;
  remark: string | null;
  testedByOid: string | null;
  testedBy: string | null;
  enteredBy: string | null;
  dateEntered: string | null;
  modifiedBy: string | null;
  modifiedAt: string | null;
  /** False when another user created the entry — G5 lets only the creator edit it (server-enforced). */
  canEdit: boolean;
  /** The entry's attachment (dbo.FileData), when it has one. */
  attachOid: string | null;
}

/** Live calculation + pass/fail for the entry form. */
export interface DailyQcEvaluation {
  calc: Record<string, string | null>;
  accept: Record<string, boolean | null>;
  status: DailyQcStatus;
  missing: string[];
}

export interface DailyQcEntryInput {
  values: Record<string, string | null>;
  remark: string | null;
  testedBy: string | null;
  /** The file uploaded for the Attachment field (null = none). */
  attachOid?: string | null;
}

export interface DailyQcAnalyst { oid: string; name: string }

/** Audit FormName (§14b.2 registry) and the legacy NavigationId the rights come from. */
export const DAILY_QC_FORM = 'Daily QC';
export const DAILY_QC_NAV_ID = 'DailyQC';

const BASE = '/api/v1/daily-qc';
const SILENT = { silentLoading: true } as never;

type Envelope<T> = { data: T };

export async function fetchLogbooks(): Promise<DailyQcLogbook[]> {
  const r = await api.get(`${BASE}/logbooks`);
  return (r.data as Envelope<DailyQcLogbook[]>).data ?? [];
}

export async function fetchLogbook(templateId: number): Promise<DailyQcLogbookDetail> {
  const r = await api.get(`${BASE}/logbooks/${templateId}`);
  const body = r.data as {
    data: Omit<DailyQcLogbookDetail, 'defaults' | 'limits' | 'isActive'>;
    defaults: Record<string, string | null>;
    limits: Record<string, DailyQcLimit[]>;
    isActive: boolean;
  };
  return { ...body.data, defaults: body.defaults ?? {}, limits: body.limits ?? {}, isActive: body.isActive };
}

export async function fetchEntries(templateId: number, from: string | null, to: string | null): Promise<DailyQcEntry[]> {
  const r = await api.get(`${BASE}/logbooks/${templateId}/entries`, { params: { from: from || undefined, to: to || undefined } });
  return (r.data as Envelope<DailyQcEntry[]>).data ?? [];
}

/** The range the server applied — a preset is resolved to its calendar-month start there. */
export interface DailyQcRange { period: string | null; from: string | null; to: string | null }

/** History entries for a preset (`period`) or a typed From/To, plus the range the server used. */
export async function fetchHistory(templateId: number, q: { period?: string; from?: string; to?: string })
  : Promise<{ entries: DailyQcEntry[]; range: DailyQcRange }> {
  const params = q.period ? { period: q.period } : { from: q.from || undefined, to: q.to || undefined };
  const r = await api.get(`${BASE}/logbooks/${templateId}/entries`, { params });
  const body = r.data as { data: DailyQcEntry[]; range: DailyQcRange };
  return { entries: body.data ?? [], range: body.range ?? { period: null, from: null, to: null } };
}

export async function fetchEntry(oid: string): Promise<DailyQcEntry> {
  const r = await api.get(`${BASE}/entries/${oid}`);
  return (r.data as Envelope<DailyQcEntry>).data;
}

/** Background evaluation while typing — silent so the global loader never flickers. */
export async function evaluateEntry(templateId: number, input: DailyQcEntryInput): Promise<DailyQcEvaluation> {
  const r = await api.post(`${BASE}/logbooks/${templateId}/evaluate`, input, SILENT);
  return (r.data as Envelope<DailyQcEvaluation>).data;
}

export async function createEntry(templateId: number, input: DailyQcEntryInput): Promise<{ oid: string; dailyQcId: string; status: DailyQcStatus }> {
  const r = await api.post(`${BASE}/logbooks/${templateId}/entries`, input);
  return (r.data as Envelope<{ oid: string; dailyQcId: string; status: DailyQcStatus }>).data;
}

export async function updateEntry(oid: string, input: DailyQcEntryInput): Promise<void> {
  await api.put(`${BASE}/entries/${oid}`, input);
}

/** Soft delete — the caller wraps it in runWithAuditReason (the API refuses a delete without a reason). */
export async function deleteEntry(oid: string): Promise<void> {
  await api.delete(`${BASE}/entries/${oid}`);
}

/** Uploads the Attachment field's file (stored in dbo.FileData, which G5 reads too). */
export async function uploadFile(file: File): Promise<{ oid: string; fileName: string }> {
  const form = new FormData();
  form.append('file', file);
  const r = await api.post(`${BASE}/files`, form);
  return (r.data as Envelope<{ oid: string; fileName: string }>).data;
}

/** Downloads an entry's attachment through the authenticated client. */
export async function downloadAttachment(entryOid: string, fileName: string | null): Promise<void> {
  const r = await api.get(`${BASE}/entries/${entryOid}/attachment`, { responseType: 'blob' });
  const url = URL.createObjectURL(r.data as Blob);
  const a = document.createElement('a');
  a.href = url; a.download = fileName || 'attachment';
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export async function fetchAnalysts(): Promise<DailyQcAnalyst[]> {
  const r = await api.get(`${BASE}/analysts`, SILENT);
  return (r.data as Envelope<DailyQcAnalyst[]>).data ?? [];
}

/** True for a 400 — the API refused the input: a validation message, shown with the shared
 *  Warning popup (`notify.warning`) like every other form, not the red Error popup. */
export function isValidationError(e: unknown): boolean {
  return (e as { response?: { status?: number } })?.response?.status === 400;
}

/** The server's message(s) from an error envelope — VALIDATION returns every problem in `details`. */
export function errorText(e: unknown, fallback: string): string {
  const err = (e as { response?: { data?: { error?: { message?: string; details?: unknown } } } })?.response?.data?.error;
  if (Array.isArray(err?.details) && err.details.length > 1) return (err.details as string[]).join('\n');
  return err?.message ?? fallback;
}

/** Display text of a value in the grid / audit line (checkbox ✓ / ✗, dates handled by the caller). */
export function isEmpty(v: string | null | undefined): boolean {
  return v === null || v === undefined || v.trim() === '';
}

/** A pass rule / formula shown under the caption, as the prototype does ("Pass: CT ≥ 15", "= T2-T1"). */
export function ruleText(f: DailyQcField): string {
  if (f.accept) return 'Pass: ' + f.accept.replace(/\s*AND\s*/gi, ' & ').replace(/>=/g, '≥').replace(/<=/g, '≤');
  if (f.calc) return '= ' + f.calc;
  return '';
}

/** A yyyy-MM-dd wire value for `n` days before today (0 = today) — an input/query value, not display. */
export function isoDaysAgo(n: number): string {
  return toWire(new Date(Date.now() - n * 86_400_000)).slice(0, 10);
}

/** History date presets, as G5 offers them (server: DailyQcPeriod). Calendar months / years —
 *  the start date is resolved by the API, never computed here. */
export const PERIODS = ['1M', '3M', '6M', '1Y', '2Y', '3Y', '5Y', '10Y', 'All'] as const;

/** G5 opens every logbook's history on 1M. */
export const DEFAULT_PERIOD = '1M';
